import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import {GpsAPI} from "@/api/gps";
import {
	GPS_STAT_METRICS,
	GPS_STAT_TEXT_FIELDS,
	GPS_STAT_FRACTION_LABEL,
} from "@/config/matchGpsStats";
import moment from "moment";

import {
	LineChart,
	Line,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	ReferenceLine,
} from "recharts";

import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";

import {GPS_FILE_IMPORT, getStoredSessions} from "@/utils/gpsSessions";
import {GPS_METRICS, acwrStatus} from "@/utils/gpsParser";
import {getAcwrConfig} from "@/utils/gpsConfig";

import StatusBadge from "@/components/StatusBadge";

import "./index.scss";
import {COLORS, SERIES} from "@/style/utils";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (val, decimals) => {
	if (val == null || isNaN(val)) return "—";
	return Number(val).toFixed(decimals ?? 0);
};

const TREND_METRICS = [
	{
		key: "distTotal",
		label: "Dist. Totale",
		unit: "m",
		decimals: 0,
		type: "bar",
		color: SERIES[0],
	},
	{
		key: "acwr",
		label: "ACWR",
		unit: "",
		decimals: 2,
		type: "line",
		color: SERIES[1],
	},
	{
		key: "maxSpeed",
		label: "Vel. Max",
		unit: "km/h",
		decimals: 1,
		type: "bar",
		color: SERIES[2],
	},
	{
		key: "movementLoad",
		label: "Mov. Load",
		unit: "",
		decimals: 0,
		type: "bar",
		color: SERIES[3],
	},
];

// ── ACWR badge ────────────────────────────────────────────────────────────────

function AcwrBadge({value, config}) {
	const s = acwrStatus(value, config);
	if (!s) return "—";
	return <StatusBadge tone={s.tone} label={fmt(value, 2) + " · " + s.label} />;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PlayerImportedGPS({playerId, seasonId}) {
	return (
		<div className="pig-wrap">
			<PlayerMatchGPS
				key={playerId + seasonId}
				playerId={playerId}
				seasonId={seasonId}
			/>
			{GPS_FILE_IMPORT && <ImportedGPSSessions playerId={playerId} />}
		</div>
	);
}

function PlayerMatchGPS({playerId, seasonId}) {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [reload, setReload] = useState(0);

	useEffect(() => {
		let active = true;
		const load = async () => {
			setLoading(true);
			setError(false);
			try {
				const response =
					playerId && seasonId
						? await GpsAPI.playerList(playerId, {seasonId})
						: [];
				if (active) setRows(response || []);
			} catch {
				if (active) {
					setRows([]);
					setError(true);
				}
			} finally {
				if (active) setLoading(false);
			}
		};
		load();
		return () => {
			active = false;
		};
	}, [playerId, seasonId, reload]);

	const metrics = GPS_STAT_METRICS.filter((m) =>
		rows.some((r) => r[m.key] != null),
	);
	const textFields = GPS_STAT_TEXT_FIELDS.filter((m) =>
		rows.some((r) => r[m.key]),
	);
	const sortedRows = [...rows].sort(
		(a, b) =>
			(b.match?.startDate || "").localeCompare(a.match?.startDate || "") ||
			a.matchId.localeCompare(b.matchId) ||
			a.fraction - b.fraction,
	);

	return (
		<section className="pig-match-gps" aria-label="GPS delle partite">
			<div className="pig-section-label">
				Dati GPS delle partite · stagione selezionata
			</div>
			{loading ? (
				<div className="pig-empty" role="status">
					Caricamento dati GPS…
				</div>
			) : error ? (
				<div className="pig-empty" role="alert">
					Impossibile caricare i dati GPS delle partite.
					<IconButton
						aria-label="Riprova caricamento GPS"
						onClick={() => setReload((v) => v + 1)}
					>
						<RefreshIcon />
					</IconButton>
				</div>
			) : !rows.length ? (
				<div className="pig-empty">
					Nessun dato GPS nelle partite di questa stagione.
				</div>
			) : (
				<div
					className="pig-table-wrap"
					tabIndex={0}
					aria-label="Rilevazioni GPS delle partite"
				>
					<table className="pig-table">
						<thead>
							<tr>
								<th className="pig-table__session-col">Partita</th>
								<th>Tempo</th>
								{textFields.map((m) => (
									<th key={m.key}>{m.label}</th>
								))}
								{metrics.map((m) => (
									<th key={m.key}>
										{m.label}
										{m.unit ? (
											<span className="pig-table__unit"> {m.unit}</span>
										) : null}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{sortedRows.map((r) => (
								<tr key={r.id}>
									<td className="pig-table__session-col">
										<Link to={"/matches/" + r.matchId}>
											<span className="pig-session-date">
												{r.match?.startDate
													? moment(r.match.startDate).format("DD/MM/YYYY")
													: "—"}
											</span>
											<span className="pig-match-label">
												{r.match?.homeClub || "—"} vs {r.match?.awayClub || "—"}
											</span>
										</Link>
									</td>
									<td>{GPS_STAT_FRACTION_LABEL(r.fraction)}</td>
									{textFields.map((m) => (
										<td key={m.key}>{r[m.key] || "—"}</td>
									))}
									{metrics.map((m) => (
										<td key={m.key}>
											{r[m.key] == null
												? "—"
												: Number(r[m.key]).toLocaleString("it-IT", {
														maximumFractionDigits: 2,
													})}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function ImportedGPSSessions({playerId}) {
	const [activeMetric, setActiveMetric] = useState("distTotal");
	const acwrConfig = useMemo(() => getAcwrConfig(), []);

	// Collect all rows for this player across all stored sessions
	const playerSessions = useMemo(() => {
		if (!playerId) return [];
		const sessions = getStoredSessions();
		const result = [];
		for (const s of sessions) {
			const row = (s.rows ?? []).find((r) => r.playerId === playerId);
			if (!row) continue;
			result.push({
				id: s.id,
				sessionType: s.sessionType,
				date: s.date,
				time: s.time,
				label: s.label,
				...row,
			});
		}
		// Sort chronologically
		return result.sort((a, b) => {
			if (!a.date) return 1;
			if (!b.date) return -1;
			return a.date.localeCompare(b.date);
		});
	}, [playerId]);

	const metric =
		TREND_METRICS.find((m) => m.key === activeMetric) ?? TREND_METRICS[0];

	const chartData = useMemo(
		() =>
			playerSessions
				.filter((s) => s[activeMetric] != null && !isNaN(s[activeMetric]))
				.map((s) => ({
					name: s.date ? moment(s.date).format("DD/MM") : "—",
					value: Number(s[activeMetric]),
					sessionType: s.sessionType,
				})),
		[playerSessions, activeMetric],
	);

	// Visible columns: only those that have at least one non-null value
	const visibleMetrics = useMemo(
		() =>
			GPS_METRICS.filter((m) =>
				playerSessions.some((s) => s[m.key] != null && !isNaN(s[m.key])),
			),
		[playerSessions],
	);

	if (playerSessions.length === 0) return null;

	return (
		<div className="pig-wrap">
			{/* ── Trend chart selector ─────────────────────────────────────── */}
			<div className="pig-section-label">Sessioni importate · Trend</div>
			<div className="pig-metric-tabs">
				{TREND_METRICS.map((m) => (
					<button
						key={m.key}
						className={
							"pig-metric-tab" +
							(activeMetric === m.key ? " pig-metric-tab--active" : "")
						}
						onClick={() => setActiveMetric(m.key)}
					>
						{m.label}
					</button>
				))}
			</div>

			{/* ── Trend chart ──────────────────────────────────────────────── */}
			{chartData.length > 1 ? (
				<div className="pig-chart">
					<ResponsiveContainer width="100%" height={180}>
						{metric.type === "line" ? (
							<LineChart
								data={chartData}
								margin={{top: 4, right: 12, left: 0, bottom: 0}}
							>
								<CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
								<XAxis
									dataKey="name"
									tick={{fontSize: 11, fill: COLORS.textSecondary}}
								/>
								<YAxis
									tick={{fontSize: 11, fill: COLORS.textSecondary}}
									width={42}
									domain={[0, "auto"]}
								/>
								<Tooltip
									formatter={(v) => [
										fmt(v, metric.decimals) +
											(metric.unit ? " " + metric.unit : ""),
										metric.label,
									]}
									contentStyle={{
										borderRadius: 8,
										border: "1px solid " + COLORS.border,
										fontSize: 12,
									}}
								/>
								{/* ACWR reference bands */}
								{activeMetric === "acwr" && (
									<>
										<ReferenceLine
											y={0.8}
											stroke={COLORS.textMuted}
											strokeDasharray="4 2"
										/>
										<ReferenceLine
											y={1.3}
											stroke={COLORS.warning}
											strokeDasharray="4 2"
										/>
										<ReferenceLine
											y={1.5}
											stroke={COLORS.critical}
											strokeDasharray="4 2"
										/>
									</>
								)}
								<Line
									type="monotone"
									dataKey="value"
									stroke={metric.color}
									strokeWidth={2}
									dot={{r: 3, fill: metric.color}}
									activeDot={{r: 5}}
								/>
							</LineChart>
						) : (
							<BarChart
								data={chartData}
								margin={{top: 4, right: 12, left: 0, bottom: 0}}
							>
								<CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
								<XAxis
									dataKey="name"
									tick={{fontSize: 11, fill: COLORS.textSecondary}}
								/>
								<YAxis
									tick={{fontSize: 11, fill: COLORS.textSecondary}}
									width={42}
								/>
								<Tooltip
									formatter={(v) => [
										fmt(v, metric.decimals) +
											(metric.unit ? " " + metric.unit : ""),
										metric.label,
									]}
									contentStyle={{
										borderRadius: 8,
										border: "1px solid " + COLORS.border,
										fontSize: 12,
									}}
								/>
								<Bar
									dataKey="value"
									fill={metric.color}
									radius={[3, 3, 0, 0]}
								/>
							</BarChart>
						)}
					</ResponsiveContainer>
				</div>
			) : null}

			{/* ── Session table ─────────────────────────────────────────────── */}
			<div className="pig-table-wrap">
				<table className="pig-table">
					<thead>
						<tr>
							<th className="pig-table__session-col">Sessione</th>
							{visibleMetrics.map((m) => (
								<th key={m.key}>
									{m.label}
									{m.unit ? (
										<span className="pig-table__unit"> {m.unit}</span>
									) : null}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{[...playerSessions].reverse().map((s, i) => (
							<tr key={i}>
								<td className="pig-table__session-col">
									<div className="pig-session-cell">
										<span
											className={
												"pig-session-icon pig-session-icon--" + s.sessionType
											}
										>
											{s.sessionType === "match" ? (
												<SportsSoccerIcon sx={{fontSize: 13}} />
											) : (
												<FitnessCenterIcon sx={{fontSize: 13}} />
											)}
										</span>
										<span className="pig-session-date">
											{s.date ? moment(s.date).format("DD MMM YYYY") : "—"}
										</span>
									</div>
								</td>
								{visibleMetrics.map((m) => (
									<td
										key={m.key}
										className={m.key === "acwr" ? "pig-table__acwr-cell" : ""}
									>
										{m.key === "acwr" ? (
											<AcwrBadge value={s[m.key]} config={acwrConfig} />
										) : (
											fmt(s[m.key], m.decimals)
										)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
